import React, { useMemo, useState } from "react";
import { Card } from "../../../../components/ui/card";
import {
  ClipboardCheck,
  AlertTriangle,
  X,
  Check,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  Clock,
  TrendingUp,
} from "lucide-react";

import {
  completeDashboardAction,
  dismissDashboardAction,
  undoDashboardAction,
  restoreDashboardAction,
} from "../../../../api/dashboardActions";

/* ---------- UI helpers ---------- */

function PriorityBadge({ priority }) {
  if (!priority) return null;

  const label = priority.toLowerCase();
  const color =
    label === "high"
      ? "bg-amber-500/15 text-amber-400 border-amber-400/30"
      : label === "medium"
      ? "bg-slate-500/10 text-slate-300 border-border/40"
      : "bg-slate-500/5 text-muted-foreground border-border/30";

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${color}`}>
      PRIORITY · {label.toUpperCase()}
    </span>
  );
}

function UrgencyBadge({ urgency }) {
  if (!urgency) return null;

  const color =
    urgency === "high"
      ? "bg-red-500/15 text-red-400 border-red-400/30"
      : urgency === "medium"
      ? "bg-amber-500/15 text-amber-400 border-amber-400/30"
      : "bg-slate-500/10 text-muted-foreground border-border/40";

  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border ${color}`}>
      URGENT
    </span>
  );
}

/* ---------- Component ---------- */

export default function ActionPlaybook({
  title = "Actions",
  subtitle = "Prioritised steps to maximise profit",
  items = [],
  onRefresh,
}) {
  const [filter, setFilter] = useState("pending");
  const [expandedId, setExpandedId] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  /* ---------- filtering ---------- */

  const filteredItems = useMemo(() => {
    let base = items;

    if (filter !== "all") {
      base = base.filter((it) => it.status === filter);
    }

    return [...base].sort(
      (a, b) => (b.priority_score ?? 0) - (a.priority_score ?? 0)
    );
  }, [items, filter]);

  /* ---------- helpers ---------- */

  const run = async (id, fn) => {
    try {
      setLoadingId(id);
      await fn(id);
      onRefresh?.();
    } finally {
      setLoadingId(null);
    }
  };

  /* ---------- render ---------- */

  return (
    <Card className="p-6 rounded-xl border border-border/60 bg-card shadow-md space-y-4">
      {/* Header */}
      <div className="flex justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-purple-400" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {["pending", "completed", "dismissed", "all"].map((key) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-3 py-1 text-sm rounded-md border ${
              filter === key
                ? "bg-purple-400 text-black"
                : "border-border/50 text-muted-foreground hover:text-foreground"
            }`}
          >
            {key.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Items / Empty state */}
      {filteredItems.length === 0 ? (
        <div className="py-12 text-center text-muted-foreground space-y-2">
          <p className="text-sm font-medium">
            No actions to show
          </p>
          <p className="text-xs">
            There are no {filter !== "all" ? filter : ""} actions at the moment.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredItems.map((it) => {
            const expanded = expandedId === it.id;
            const isDone = it.status === "completed";

            return (
              <div
                key={it.id}
                className="p-4 rounded-lg border border-border/30 bg-muted/20"
              >
                <div className="flex gap-6">
                  {/* LEFT: Info */}
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <p
                        className={`text-base font-semibold ${
                          isDone
                            ? "line-through text-muted-foreground"
                            : "text-foreground"
                        }`}
                      >
                        {it.title}
                      </p>

                      <PriorityBadge priority={it.priority} />
                      <UrgencyBadge urgency={it.timing?.urgency} />
                    </div>

                    <div className="flex items-center gap-4 text-sm">
                      {it.timing?.deadline_label && (
                        <span className="flex items-center gap-1 text-amber-300">
                          <Clock className="w-4 h-4" />
                          {it.timing.deadline_label}
                        </span>
                      )}

                      {it.expected_profit_impact != null && (
                        <span className="flex items-center gap-1 text-emerald-400 font-semibold">
                          <TrendingUp className="w-4 h-4" />
                          +{it.expected_profit_impact}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* RIGHT: Actions */}
                  <div className="flex flex-col gap-2 items-end">
                    {it.status === "pending" && (
                      <>
                        <button
                          disabled={loadingId === it.id}
                          onClick={() => run(it.id, completeDashboardAction)}
                          className="px-4 py-1.5 rounded-md bg-emerald-500 text-black text-sm font-medium hover:bg-emerald-400 transition"
                        >
                          ✓ Complete
                        </button>
                        <button
                          onClick={() => run(it.id, dismissDashboardAction)}
                          className="px-4 py-1.5 rounded-md border border-red-400/40 text-red-400 text-sm hover:bg-red-500/10 transition"
                        >
                          ✕ Dismiss
                        </button>
                      </>
                    )}
                    {it.status === "dismissed" && (
                      <button
                        onClick={() => run(it.id, restoreDashboardAction)}
                        className="px-4 py-1.5 rounded-md
                                  border border-purple-400/40
                                  text-purple-300
                                  text-sm
                                  hover:bg-purple-500/10 transition"
                      >
                        ↺ Restore
                      </button>
                    )}
                    {it.status === "completed" && (
                      <button
                        onClick={() => run(it.id, undoDashboardAction)}
                        className="px-4 py-1.5 rounded-md border border-border/40 text-sm text-muted-foreground hover:text-foreground"
                      >
                        ↺ Undo
                      </button>
                    )}
                  </div>
                </div>

                {/* Expand */}
                <button
                  onClick={() => setExpandedId(expanded ? null : it.id)}
                  className="mt-3 text-xs text-purple-400 flex items-center gap-1"
                >
                  {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {expanded ? "Hide details" : "View details"}
                </button>

                {/* Details */}
                {expanded && (
                  <div className="mt-3 text-sm text-muted-foreground space-y-2">
                    {it.detail && <p>{it.detail}</p>}

                    {Array.isArray(it.expected_impacts) && (
                      <ul className="list-disc pl-5 space-y-1">
                        {it.expected_impacts.map((imp, idx) => (
                          <li key={idx}>{imp}</li>
                        ))}
                      </ul>
                    )}

                    {it.timing?.if_delayed && (
                      <p className="text-red-400 flex items-start gap-1">
                        <AlertTriangle className="w-4 h-4 mt-0.5" />
                        {it.timing.if_delayed}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}